import React, { createContext, useContext, useEffect, useState, useCallback } from "react";

export const ThemeContext = createContext();

// localStorage key — kept in sync with the anti-flash script in public/index.html.
const STORAGE_KEY = "theme";

// Valid modes the user can pick. "system" follows the OS preference live.
const MODES = ["light", "dark", "system"];

const prefersDarkMQ = () =>
  typeof window !== "undefined" && window.matchMedia
    ? window.matchMedia("(prefers-color-scheme: dark)")
    : null;

// Read the saved mode, defaulting to "light" (the historical look).
const readStoredMode = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    return MODES.includes(saved) ? saved : "light";
  } catch {
    return "light";
  }
};

// Given a mode, resolve to the concrete theme actually shown ("light" | "dark").
const resolveTheme = (mode) => {
  if (mode === "system") {
    const mq = prefersDarkMQ();
    return mq && mq.matches ? "dark" : "light";
  }
  return mode === "dark" ? "dark" : "light";
};

// Toggle the `dark` class on <html> so Tailwind's `dark:` variants apply.
const applyThemeClass = (resolved) => {
  const root = document.documentElement;
  if (resolved === "dark") {
    root.classList.add("dark");
  } else {
    root.classList.remove("dark");
  }
};

export const ThemeProvider = ({ children }) => {
  const [mode, setModeState] = useState(readStoredMode);
  const [resolvedTheme, setResolvedTheme] = useState(() => resolveTheme(readStoredMode()));

  // Apply the resolved theme to the DOM and persist the chosen mode whenever
  // the mode changes.
  useEffect(() => {
    const resolved = resolveTheme(mode);
    setResolvedTheme(resolved);
    applyThemeClass(resolved);
    try {
      localStorage.setItem(STORAGE_KEY, mode);
    } catch {
      /* storage unavailable — non-fatal, theme just won't persist */
    }
  }, [mode]);

  // In "system" mode, react to live OS theme changes (e.g. macOS auto night).
  useEffect(() => {
    if (mode !== "system") return undefined;
    const mq = prefersDarkMQ();
    if (!mq) return undefined;
    const onChange = () => {
      const resolved = mq.matches ? "dark" : "light";
      setResolvedTheme(resolved);
      applyThemeClass(resolved);
    };
    // addEventListener is the modern API; addListener is the Safari fallback.
    if (mq.addEventListener) mq.addEventListener("change", onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", onChange);
      else mq.removeListener(onChange);
    };
  }, [mode]);

  const setMode = useCallback((next) => {
    setModeState(MODES.includes(next) ? next : "light");
  }, []);

  return (
    <ThemeContext.Provider value={{ mode, setMode, resolvedTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

// Convenience hook. `mode` is the user's choice (light/dark/system);
// `resolvedTheme` is what's actually rendered (light/dark).
export const useTheme = () => {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return ctx;
};
